import moment from 'moment-timezone';

/*
 * Sample data for the Knowledge Review tab outside production (design review before the
 * backend lands). Shapes match the API contract in
 * cplanetbackend/aidocs/plans/2026-09-24-knowledge-review-inbox.md. The scenarios mirror
 * what the interaction-KB builds kept finding: team answers that disagree, a question that
 * really depends on the booking, a cancellation window the KB and the team disagree on, a
 * moved meeting point, and a seasonal answer that expires.
 */

const ago = (n, unit = 'days') => moment().subtract(n, unit).toISOString();
const ahead = (n, unit = 'days') => moment().add(n, unit).format('YYYY-MM-DD');

// Tag scoping (tagScope.js). System tags by category; which categories are featured is the
// operator's config (ai_featured_tag_categories, else ai_collection_category), read by the mock
// the way the backend reads it. Every other category is offered under "+ Other tag".
const COLLECTION = { code: 'collection', name: 'Collection' };
const DESTINATION = { code: 'destination', name: 'Destination' };
const THEME = { code: 'theme', name: 'Theme' };
const LANGUAGE = { code: 'language', name: 'Language' };

export const SAMPLE_TAGS = [
  { id: 901, name: 'Colosseum', category: COLLECTION },
  { id: 902, name: 'Vatican', category: COLLECTION },
  { id: 903, name: 'Food Tours', category: COLLECTION },
  { id: 904, name: 'Day Trips', category: COLLECTION },
  { id: 911, name: 'Rome', category: DESTINATION },
  { id: 912, name: 'Amalfi Coast', category: DESTINATION },
  { id: 921, name: 'Ancient sites', category: THEME },
  { id: 922, name: 'Evening', category: THEME },
  { id: 931, name: 'Spanish', category: LANGUAGE },
];

export const SAMPLE_ACCESS_TAGS = [
  { id: 951, name: 'Rome office' },
  { id: 952, name: 'Naples office' },
];

// Each language version of a tour is its own product (as at Crown Tours), so one collection
// holds several.
export const SAMPLE_PRODUCTS = [
  { id: 101, name: 'Colosseum Underground & Ancient Rome', tag_ids: [901, 911, 921], atag_ids: [951] },
  { id: 102, name: 'Vatican Museums & Sistine Chapel', tag_ids: [902, 911], atag_ids: [951] },
  { id: 103, name: 'Trastevere Food Tour', tag_ids: [903, 911, 922], atag_ids: [951] },
  { id: 104, name: 'Pompeii & Amalfi Coast Day Trip', tag_ids: [904, 912, 921], atag_ids: [952] },
  { id: 105, name: 'Rome at Twilight Walking Tour', tag_ids: [911, 922], atag_ids: [951] },
  { id: 106, name: 'Colosseum Underground & Ancient Rome (Spanish)', tag_ids: [901, 911, 921, 931], atag_ids: [951] },
  { id: 107, name: 'Vatican Museums & Sistine Chapel (Spanish)', tag_ids: [902, 911, 931], atag_ids: [951] },
  { id: 108, name: 'Colosseum Arena Floor', tag_ids: [901, 911, 921], atag_ids: [951] },
  { id: 109, name: 'Amalfi Coast Boat Day', tag_ids: [904, 912], atag_ids: [952] },
];

const P = Object.fromEntries(SAMPLE_PRODUCTS.map((p) => [p.id, p]));
const T = Object.fromEntries(SAMPLE_TAGS.map((t) => [t.id, t]));
const brief = ({ id, name }) => ({ id, name });

const VATICAN_SUNDAYS = {
  id: 201,
  question: 'Are the Vatican Museums open on Sundays?',
  answer: 'The Vatican Museums are closed on Sundays except the last Sunday of the month, when entry is free (and very busy). We don’t run tours on those days.',
  all_tours: false,
  tours: [P[102]],
  channels: ['support', 'widget'],
  valid_until: moment().subtract(1, 'days').format('YYYY-MM-DD'),
  valid_until_set_by: 'Giulia',
  valid_until_reason: null,
  approved_by: 'Giulia',
  approved_ts: ago(160),
  last_confirmed_ts: ago(160),
};

export const SAMPLE_ITEMS = [
  {
    id: 1,
    kind: 'gap',
    status: 'open',
    channel: 'support',
    question: 'Is the Colosseum tour wheelchair accessible?',
    ask_count: 14,
    first_asked_ts: ago(41),
    last_asked_ts: ago(2),
    tours: [P[101]],
    booking_specific_hint: false,
    team_answers: [
      {
        text: 'The arena floor and the first tier are accessible by lift, but the underground levels are only reachable by stairs, so we can’t take wheelchairs there. We can move you to our standard Colosseum tour at no extra cost — just let us know.',
        count: 9,
        first_ts: ago(41),
        last_ts: ago(2),
        agents: ['Giulia', 'Marco'],
      },
      {
        text: 'Unfortunately the tour is not suitable for wheelchairs.',
        count: 3,
        first_ts: ago(30),
        last_ts: ago(19),
        agents: ['Sam'],
      },
    ],
    examples: [
      { text: 'Hi, my mother uses a wheelchair — can she join the Colosseum underground tour?', asked_ts: ago(2), tour: P[101].name, source: 'WhatsApp' },
      { text: 'is the colosseum accessible for someone who can’t do stairs', asked_ts: ago(6), tour: P[101].name, source: 'Web chat widget' },
      { text: 'Do you have elevators at the Colosseum? My husband has a mobility scooter.', asked_ts: ago(11), tour: P[101].name, source: 'Email' },
      { text: 'Wheelchair access?', asked_ts: ago(23), tour: P[101].name, source: 'SMS' },
    ],
  },
  {
    id: 2,
    kind: 'gap',
    status: 'open',
    channel: 'support',
    question: 'What time is my hotel pickup for the Pompeii trip?',
    ask_count: 18,
    first_asked_ts: ago(30),
    last_asked_ts: ago(1),
    tours: [P[104]],
    booking_specific_hint: true,
    team_answers: [
      {
        text: 'Your pickup is at 7:15 from the front entrance of your hotel.',
        count: 6,
        last_ts: ago(1),
        agents: ['Marco'],
      },
      {
        text: 'Pickup is between 6:45 and 7:30 depending on your hotel — your exact time is on your confirmation email.',
        count: 5,
        last_ts: ago(8),
        agents: ['Giulia'],
      },
    ],
    examples: [
      { text: 'What time will you pick us up tomorrow for Pompeii? We’re at the Hotel Artemide', asked_ts: ago(1), tour: P[104].name, source: 'WhatsApp' },
      { text: 'pickup time for amalfi tour?', asked_ts: ago(4), tour: P[104].name, source: 'SMS' },
      { text: 'Hello, I didn’t get a pickup time for Saturday’s trip', asked_ts: ago(9), tour: P[104].name, source: 'Email' },
    ],
  },
  {
    id: 3,
    kind: 'conflict',
    status: 'open',
    channel: 'support',
    question: 'How late can I cancel for a full refund?',
    ask_count: 27,
    first_asked_ts: ago(112),
    last_asked_ts: ago(1),
    tours: [P[101], P[102], P[103], P[104]],
    booking_specific_hint: false,
    kb_excerpt: {
      doc_id: 41,
      doc_name: 'Rome_FAQ_2025.pdf',
      text: 'Free cancellation up to 24 hours before the tour start time.',
    },
    team_answers: [
      {
        text: 'You can cancel free of charge up to 48 hours before your tour starts.',
        count: 21,
        last_ts: ago(1),
        agents: ['Giulia', 'Marco', 'Sam'],
        tours: [P[102], P[103], P[104], P[105]],
      },
      {
        text: 'For the Colosseum Underground tour the cancellation window is 72 hours, because the underground tickets are non-refundable after that.',
        count: 6,
        last_ts: ago(5),
        agents: ['Giulia'],
        tours: [P[101]],
      },
    ],
    team_since_ts: ago(112),
    examples: [
      { text: 'If I cancel tomorrow morning (tour is Friday) do I get my money back?', asked_ts: ago(1), tour: P[103].name, source: 'WhatsApp' },
      { text: 'what is your cancelation policy', asked_ts: ago(3), tour: P[102].name, source: 'Web chat widget' },
      { text: 'Can I still cancel the underground tour on Thursday? It’s on Saturday.', asked_ts: ago(5), tour: P[101].name, source: 'Email' },
    ],
  },
  {
    id: 4,
    kind: 'conflict',
    status: 'open',
    channel: 'support',
    question: 'Where do we meet for the Trastevere food tour?',
    ask_count: 19,
    first_asked_ts: ago(43),
    last_asked_ts: ago(3),
    tours: [P[103]],
    booking_specific_hint: false,
    kb_excerpt: {
      doc_id: 44,
      doc_name: 'Food tours – meeting points.docx',
      text: 'Meet your guide at the fountain in Piazza di Santa Maria in Trastevere.',
    },
    team_answers: [
      {
        text: 'Meet your guide at the top of the steps in Piazza Trilussa, by the fountain — look for the green umbrella.',
        count: 19,
        last_ts: ago(3),
        agents: ['Giulia', 'Sam'],
      },
    ],
    team_since_ts: ago(43),
    examples: [
      { text: 'Where exactly is the meeting point for tonight’s food tour?', asked_ts: ago(3), tour: P[103].name, source: 'WhatsApp' },
      { text: 'We’re at Santa Maria in Trastevere and can’t see anyone??', asked_ts: ago(12), tour: P[103].name, source: 'SMS' },
    ],
  },
  {
    id: 5,
    kind: 'gap',
    status: 'open',
    channel: 'support',
    question: 'Can I bring a backpack or luggage on the Vatican tour?',
    ask_count: 11,
    first_asked_ts: ago(55),
    last_asked_ts: ago(4),
    tours: [P[102]],
    booking_specific_hint: false,
    team_answers: [
      {
        text: 'Small backpacks are fine. Large bags, suitcases and umbrellas must be left at the free cloakroom before security, and you collect them at the same entrance — so plan to exit the way you came in.',
        count: 11,
        last_ts: ago(4),
        agents: ['Marco', 'Sam'],
      },
    ],
    examples: [
      { text: 'We’re coming straight from the airport — can we bring our suitcases to the Vatican?', asked_ts: ago(4), tour: P[102].name, source: 'WhatsApp' },
      { text: 'backpack allowed in sistine chapel?', asked_ts: ago(15), tour: P[102].name, source: 'Web chat widget' },
    ],
  },
  {
    id: 6,
    kind: 'gap',
    status: 'open',
    channel: 'support',
    question: 'Is there a vegetarian option on the food tour?',
    ask_count: 9,
    first_asked_ts: ago(60),
    last_asked_ts: ago(6),
    tours: [P[103]],
    booking_specific_hint: false,
    team_answers: [
      {
        text: 'Yes! Every stop has a vegetarian option. Just tell your guide at the start of the tour. We can’t guarantee a fully vegan or gluten-free menu, sorry.',
        count: 9,
        last_ts: ago(6),
        agents: ['Giulia'],
      },
    ],
    examples: [
      { text: 'My daughter is vegetarian, is that a problem for the food tour?', asked_ts: ago(6), tour: P[103].name, source: 'Email' },
      { text: 'veggie options?', asked_ts: ago(20), tour: P[103].name, source: 'Web chat widget' },
    ],
  },
  {
    id: 7,
    kind: 'gap',
    status: 'open',
    channel: 'widget',
    question: 'Do you offer private tours?',
    ask_count: 9,
    first_asked_ts: ago(25),
    last_asked_ts: ago(2),
    tours: [],
    booking_specific_hint: false,
    team_answers: [
      {
        text: 'Yes — all of our tours can be booked privately for your group. Send us your date, group size and the tour you’re interested in, and we’ll reply with a quote within one working day.',
        count: 4,
        last_ts: ago(2),
        agents: ['Sam'],
      },
    ],
    examples: [
      { text: 'Do you do private tours of the Vatican for a family of 6?', asked_ts: ago(2), tour: null, source: 'Web chat widget' },
      { text: 'private guide possible?', asked_ts: ago(9), tour: null, source: 'Web chat widget' },
    ],
  },
  {
    id: 11,
    kind: 'gap',
    status: 'open',
    channel: 'widget',
    question: 'Which tour is best for a first visit to Rome?',
    ask_count: 16,
    first_asked_ts: ago(38),
    last_asked_ts: ago(1),
    tours: [],
    booking_specific_hint: false,
    team_answers: [
      {
        text: 'If it’s your first time, we’d start with the Colosseum Underground & Ancient Rome tour: it covers the Colosseum, the Forum and Palatine Hill in one morning. Add the Vatican Museums & Sistine Chapel on another day and you’ve seen the two big ones with a guide.',
        count: 7,
        last_ts: ago(1),
        agents: ['Sam', 'Giulia'],
      },
    ],
    examples: [
      { text: 'first time in rome, 3 days, which tour should we do?', asked_ts: ago(1), tour: null, source: 'Web chat widget' },
      { text: 'What’s your most popular tour?', asked_ts: ago(5), tour: null, source: 'Web chat widget' },
      { text: 'We only have one day — Colosseum or Vatican?', asked_ts: ago(12), tour: null, source: 'Web chat widget' },
    ],
  },
  {
    id: 12,
    kind: 'gap',
    status: 'open',
    channel: 'widget',
    question: 'Are there spots left on tomorrow’s Colosseum tour?',
    ask_count: 12,
    first_asked_ts: ago(29),
    last_asked_ts: ago(0, 'days'),
    tours: [P[101]],
    booking_specific_hint: true,
    team_answers: [
      {
        text: 'Yes, we still have 4 spots on the 9:30 tour tomorrow.',
        count: 5,
        last_ts: ago(0, 'days'),
        agents: ['Marco'],
      },
    ],
    examples: [
      { text: 'any availability tomorrow morning for the colosseum underground?', asked_ts: ago(0, 'days'), tour: P[101].name, source: 'Web chat widget' },
      { text: 'Is Saturday sold out?', asked_ts: ago(3), tour: P[101].name, source: 'Web chat widget' },
    ],
  },
  {
    id: 13,
    kind: 'gap',
    status: 'open',
    channel: 'widget',
    question: 'Is there a discount for groups?',
    ask_count: 8,
    first_asked_ts: ago(47),
    last_asked_ts: ago(4),
    tours: [],
    booking_specific_hint: false,
    team_answers: [
      {
        text: 'Groups of 10 or more get 10% off any tour. Send us your date and group size and we’ll set up the booking for you.',
        count: 4,
        last_ts: ago(4),
        agents: ['Sam'],
      },
    ],
    examples: [
      { text: 'We’re a school group of 24, do you do group rates?', asked_ts: ago(4), tour: null, source: 'Web chat widget' },
      { text: 'group discount?', asked_ts: ago(16), tour: null, source: 'Web chat widget' },
    ],
  },
  {
    id: 14,
    kind: 'conflict',
    status: 'open',
    channel: 'widget',
    question: 'Do you offer hotel pickup?',
    ask_count: 10,
    first_asked_ts: ago(64),
    last_asked_ts: ago(2),
    tours: [P[104]],
    booking_specific_hint: false,
    kb_excerpt: {
      doc_id: 52,
      doc_name: 'Website FAQ.pdf',
      text: 'We do not offer hotel pickup. All tours start at the meeting point shown on your ticket.',
    },
    team_answers: [
      {
        text: 'Hotel pickup is included on the Pompeii & Amalfi Coast day trip for hotels inside the city centre. Our walking tours all start at a meeting point.',
        count: 10,
        last_ts: ago(2),
        agents: ['Sam', 'Marco'],
        tours: [P[104]],
      },
    ],
    team_since_ts: ago(64),
    examples: [
      { text: 'Do you pick up from hotels for the Amalfi trip?', asked_ts: ago(2), tour: P[104].name, source: 'Web chat widget' },
      { text: 'is transport from our hotel included', asked_ts: ago(9), tour: null, source: 'Web chat widget' },
    ],
  },
  {
    id: 15,
    kind: 'stale',
    status: 'open',
    channel: 'widget',
    question: 'Do you have any promo codes?',
    ask_count: 0,
    tours: [],
    stale_reason: 'expired',
    answer: {
      id: 208,
      question: 'Do you have any promo codes?',
      answer: 'Yes! Use SUMMER10 at checkout for 10% off any tour booked before August 31.',
      all_tours: true,
      tours: [],
      channels: ['widget'],
      valid_until: moment().subtract(24, 'days').format('YYYY-MM-DD'),
      valid_until_set_by: 'Sam',
      valid_until_reason: 'before August 31',
      approved_by: 'Sam',
      approved_ts: ago(95),
      last_confirmed_ts: ago(95),
    },
    examples: [],
    team_answers: [],
  },
  {
    // Team vs team, at the same time: a real disagreement.
    id: 16,
    kind: 'conflict',
    status: 'open',
    channel: 'support',
    question: 'How early should we arrive for the Vatican tour?',
    ask_count: 13,
    first_asked_ts: ago(80),
    last_asked_ts: ago(1),
    tours: [P[102]],
    booking_specific_hint: false,
    kb_excerpt: null,
    team_answers: [
      {
        text: 'Please arrive 15 minutes before your tour starts, at the meeting point on Viale Vaticano.',
        count: 14,
        first_ts: ago(80),
        last_ts: ago(1),
        agents: ['Giulia', 'Marco'],
      },
      {
        text: 'Please be at the meeting point 30 minutes early. Security at the Vatican can take a while.',
        count: 9,
        first_ts: ago(70),
        last_ts: ago(3),
        agents: ['Sam'],
      },
    ],
    examples: [
      { text: 'How early do we need to be there for the Vatican tomorrow?', asked_ts: ago(1), tour: P[102].name, source: 'WhatsApp' },
      { text: 'what time should we arrive', asked_ts: ago(7), tour: P[102].name, source: 'SMS' },
    ],
  },
  {
    // Team vs team, one after the other: the answer CHANGED (cash -> pay by link). The
    // newer, less-used answer is the right one, which is why this isn't "pick the most used".
    id: 17,
    kind: 'conflict',
    status: 'open',
    channel: 'support',
    question: 'Can I pay for the food tour in cash on the day?',
    ask_count: 11,
    first_asked_ts: ago(150),
    last_asked_ts: ago(2),
    tours: [P[103]],
    booking_specific_hint: false,
    kb_excerpt: null,
    team_answers: [
      {
        text: 'Yes, you can pay your guide in cash (euros) at the start of the tour.',
        count: 12,
        first_ts: ago(150),
        last_ts: ago(42),
        agents: ['Marco', 'Giulia'],
      },
      {
        text: 'We’ve stopped taking cash. Please pay using the payment link in your confirmation email.',
        count: 7,
        first_ts: ago(40),
        last_ts: ago(2),
        agents: ['Giulia', 'Sam'],
      },
    ],
    examples: [
      { text: 'Can I just pay cash to the guide?', asked_ts: ago(2), tour: P[103].name, source: 'WhatsApp' },
      { text: 'do you take cash on the food tour', asked_ts: ago(15), tour: P[103].name, source: 'Email' },
    ],
  },
  {
    id: 18,
    kind: 'conflict',
    status: 'open',
    channel: 'widget',
    question: 'How long is the Pompeii & Amalfi day trip?',
    ask_count: 7,
    first_asked_ts: ago(60),
    last_asked_ts: ago(3),
    tours: [P[104]],
    booking_specific_hint: false,
    kb_excerpt: null,
    team_answers: [
      {
        text: 'It’s a full day: about 12 hours, leaving Rome at 7:00 and back around 19:00.',
        count: 5,
        first_ts: ago(60),
        last_ts: ago(3),
        agents: ['Sam'],
      },
      {
        text: 'About 11 hours door to door.',
        count: 3,
        first_ts: ago(45),
        last_ts: ago(6),
        agents: ['Marco'],
      },
    ],
    examples: [
      { text: 'how long is the amalfi trip?', asked_ts: ago(3), tour: P[104].name, source: 'Web chat widget' },
      { text: 'What time do we get back to Rome from Pompeii?', asked_ts: ago(11), tour: P[104].name, source: 'Web chat widget' },
    ],
  },
  {
    id: 8,
    kind: 'gap',
    status: 'open',
    channel: 'support',
    question: 'Do children need a ticket, and is there a child price?',
    ask_count: 6,
    first_asked_ts: ago(33),
    last_asked_ts: ago(10),
    tours: [P[101], P[102]],
    booking_specific_hint: false,
    team_answers: [],
    examples: [
      { text: 'Does my 3 year old need a ticket for the Colosseum?', asked_ts: ago(10), tour: P[101].name, source: 'WhatsApp' },
      { text: 'Is there a discount for kids under 12 at the Vatican?', asked_ts: ago(17), tour: P[102].name, source: 'Email' },
    ],
  },
  {
    id: 9,
    kind: 'stale',
    status: 'open',
    channel: 'support',
    question: 'Are the Vatican Museums open on Sundays?',
    ask_count: 0,
    tours: [P[102]],
    stale_reason: 'expired',
    answer: VATICAN_SUNDAYS,
    // Used by both chatbots, so it has a card in each inbox.
    twins: [{ id: 19, channel: 'widget' }],
    examples: [],
    team_answers: [],
  },
  {
    id: 19,
    kind: 'stale',
    status: 'open',
    channel: 'widget',
    question: 'Are the Vatican Museums open on Sundays?',
    ask_count: 0,
    tours: [P[102]],
    stale_reason: 'expired',
    answer: VATICAN_SUNDAYS,
    // Used by both chatbots, so it has a card in each inbox.
    twins: [{ id: 9, channel: 'support' }],
    examples: [],
    team_answers: [],
  },
  {
    id: 10,
    kind: 'stale',
    status: 'open',
    channel: 'support',
    question: 'Is lunch included on the day trips?',
    ask_count: 0,
    tours: [P[104]],
    stale_reason: 'not_confirmed',
    answer: {
      id: 202,
      question: 'Is lunch included on the day trips?',
      answer: 'Yes, a two-course lunch with a drink is included on all our day trips from Naples.',
      all_tours: false,
      tours: [],
      access_tags: [SAMPLE_ACCESS_TAGS[1]],
      channels: ['support'],
      valid_until: null,
      approved_by: 'Marco',
      approved_ts: ago(130),
      last_confirmed_ts: ago(130),
    },
    examples: [],
    team_answers: [],
  },
];

export const SAMPLE_ANSWERS = [
  {
    // A destination, minus the products it doesn't apply to (Nhue's mandatory ID documents).
    id: 203,
    question: 'Do I need to bring a photo ID?',
    answer: 'Yes, please bring a passport or ID card for everyone in your group. The Colosseum and the Vatican check names against the tickets at the entrance.',
    all_tours: false,
    tours: [],
    collections: [T[911]],
    excluded_tours: [brief(P[103]), brief(P[105])],
    channels: ['support', 'widget'],
    valid_until: null,
    approved_by: 'Giulia',
    approved_ts: ago(21),
    last_confirmed_ts: ago(21),
  },
  {
    id: 204,
    question: 'Is there a dress code for the Vatican?',
    answer: 'Yes. Shoulders and knees must be covered for everyone, including children. Guests who aren’t covered can be refused entry and we can’t refund the ticket.',
    all_tours: false,
    tours: [],
    collections: [T[902]],
    channels: ['support', 'widget'],
    valid_until: null,
    approved_by: 'Sam',
    approved_ts: ago(48),
    last_confirmed_ts: ago(48),
  },
  {
    id: 205,
    question: 'Do tours run in the rain?',
    answer: 'Yes, all tours run rain or shine. Bring an umbrella or a light rain jacket. We only cancel for severe weather warnings, and you’ll get a full refund if we do.',
    // The boat day depends on the sea, not the rain.
    all_tours: true,
    tours: [],
    excluded_tours: [brief(P[109])],
    channels: ['support', 'widget'],
    valid_until: null,
    approved_by: 'Marco',
    approved_ts: ago(75),
    last_confirmed_ts: ago(12),
  },
  {
    id: 206,
    question: 'Is the Twilight tour running in October?',
    answer: 'Yes, the Rome at Twilight tour runs every evening until October 31, starting at 18:30. From November it starts at 17:00.',
    all_tours: false,
    tours: [P[105]],
    channels: ['support', 'widget'],
    valid_until: '2026-10-31',
    valid_until_set_by: 'Giulia',
    valid_until_reason: 'until October 31',
    approved_by: 'Giulia',
    approved_ts: ago(9),
    last_confirmed_ts: ago(9),
  },
  {
    id: 209,
    question: 'Can I book a tour for today?',
    answer: 'Yes, as long as there are spots left. Online booking stays open until 2 hours before each tour starts.',
    all_tours: true,
    tours: [],
    channels: ['widget'],
    valid_until: null,
    approved_by: 'Sam',
    approved_ts: ago(14),
    last_confirmed_ts: ago(14),
  },
  {
    id: 207,
    question: 'Can I pay in cash on the day?',
    answer: 'No — all tours must be paid online when you book. We don’t accept cash or card payments on the day.',
    all_tours: true,
    tours: [],
    channels: ['support'],
    valid_until: ahead(365),
    approved_by: 'Sam',
    approved_ts: ago(33),
    last_confirmed_ts: ago(33),
  },
];

/*
 * Long tail: what a real queue looks like after clustering. Mostly questions asked once or
 * twice, a few asked more. Built deterministically so the preview is stable; tests use the
 * curated set above only (resetKnowledgeReviewMock({ longTail: false })).
 * Each entry: [question, product ids ([] = none), team answer or null].
 */
const LONG_TAIL_SUPPORT = [
  ['Is there a toilet at the meeting point?', [101], 'There are public toilets at the Colosseum metro station, two minutes from the meeting point. There are also toilets inside the Colosseum after security.'],
  ['Can I bring a stroller on the tour?', [101, 102], 'Foldable strollers are fine on the Vatican tour. For the Colosseum Underground we recommend a baby carrier, because there are stairs.'],
  ['Is the tour available in Spanish?', [], 'We run Spanish tours on Tuesdays, Thursdays and Saturdays. Reply with your booking and we’ll check if we can move you.'],
  ['Can we join the tour halfway?', [], null],
  ['Do I need to bring my passport?', [101, 102], 'Please bring a photo ID. The Colosseum checks that names match the tickets.'],
  ['Is drinking water available on the tour?', [101, 103], 'There are free drinking fountains all over Rome. Bring a refillable bottle and your guide will point them out.'],
  ['Can I change the date of my tour?', [], null],
  ['Is the tour suitable for someone with a bad knee?', [101, 104], null],
  ['What happens if my flight is delayed?', [104], null],
  ['Can I bring my dog?', [], 'Sorry, only guide dogs can join our tours.'],
  ['Is there Wi-Fi on the bus?', [104], 'Yes, the coach has free Wi-Fi, but the signal drops along the coast.'],
  ['How far do we walk on the tour?', [101, 105], 'About 3 km at an easy pace, with stops. Wear comfortable shoes.'],
  ['Can I take photos inside the Sistine Chapel?', [102], 'Photos aren’t allowed in the Sistine Chapel itself, but you can take photos everywhere else in the museums.'],
  ['Are headsets provided?', [102], 'Yes, everyone gets a headset so you can hear your guide in the busy galleries.'],
  ['Is the food tour OK for someone with a nut allergy?', [103], null],
  ['Do we get a certificate or souvenir?', [], null],
  ['Can I get an invoice for my company?', [], 'Of course. Reply with your company name, address and VAT number and we’ll email it to you.'],
  ['Is the meeting point accessible by metro?', [101], 'Yes, take metro line B to Colosseo. The meeting point is right across from the exit.'],
  ['Can I bring a small suitcase on the Pompeii trip?', [104], 'Yes, there’s space for one small suitcase per person under the coach.'],
  ['Is the Twilight tour safe for solo travellers?', [105], null],
  ['Do you have tours on public holidays?', [], null],
  ['Can I tip the guide by card?', [], null],
  ['Is there a minimum age for the Pompeii trip?', [104], 'No minimum age, but it is a long day, so we recommend it for children 6 and up.'],
  ['What should I wear to the Vatican in summer?', [102], 'Light clothing that covers shoulders and knees. A scarf works well to cover shoulders.'],
  ['Can I leave the tour early?', [], null],
  ['Do you offer student discounts?', [102], null],
  ['Will we see the Pope?', [102], null],
  ['Is lunch vegetarian-friendly on the Pompeii trip?', [104], null],
  ['Can I bring my own snacks on the food tour?', [103], null],
  ['Is the Colosseum tour in the shade?', [101], null],
  ['What if it’s very hot?', [], null],
  ['Can I book the underground without the Forum?', [101], null],
  ['Is there a cloakroom at the Colosseum?', [101], 'No, the Colosseum has no cloakroom. Large bags aren’t allowed, so please travel light.'],
  ['Do kids get their own headset?', [102], null],
  ['Is the boat ride on the Amalfi trip included?', [104], null],
  ['Can we swim on the Amalfi trip?', [104], null],
  ['How big are the groups?', [], 'Our small-group tours have a maximum of 20 people.'],
  ['Is the tour wheelchair accessible at the Vatican?', [102], null],
  ['Do we queue for security?', [101, 102], 'Your guide takes you through the group entrance, but everyone still goes through security. It usually takes 10–20 minutes.'],
  ['What if I lose my ticket?', [], null],
  ['Is there parking near the meeting point?', [], null],
  ['Can I bring a selfie stick?', [101, 102], null],
  ['Are there benches to rest on?', [101, 102], null],
  ['Is wine included on the food tour?', [103], 'Yes, the food tour includes two glasses of local wine for adults. Soft drinks for everyone else.'],
  ['Can I switch to an earlier time slot?', [], null],
  ['Do you speak German?', [], null],
  ['Is there an elevator in the Vatican Museums?', [102], null],
  ['Can pregnant women join the underground tour?', [101], null],
  ['What’s the best time of day for the Colosseum?', [101], null],
  ['Can I get a refund if it rains?', [], null],
];

const LONG_TAIL_WIDGET = [
  ['Do you have gift vouchers?', [], 'Yes! Gift vouchers are available for any amount and valid for 12 months. Send us a message and we’ll set one up.'],
  ['Can I book for a group of 30?', [], null],
  ['Do you do tours at night?', [105], 'Yes, our Rome at Twilight Walking Tour starts at 18:30 until October.'],
  ['Which tour is best for kids?', [], null],
  ['Is it cheaper to book on your website?', [], null],
  ['Do you run tours in Florence?', [], 'Not at the moment. Our tours are all in and around Rome.'],
  ['How do I get my tickets after booking?', [], 'You’ll get your tickets by email right after booking. Just show them on your phone.'],
  ['Are your guides licensed?', [], 'Yes, all of our guides are officially licensed by the Lazio region.'],
  ['Can I pay with PayPal?', [], null],
  ['Do you offer Colosseum tours with the arena floor?', [101], null],
  ['How long is the food tour?', [103], 'About 3.5 hours, with 6 tasting stops.'],
  ['Is the Pompeii trip worth it?', [104], null],
  ['Can I customise a private tour?', [], null],
  ['Do you do hen party tours?', [], null],
  ['Is there a tour that combines Vatican and Colosseum?', [101, 102], null],
  ['What is your cancellation policy?', [], null],
  ['Can I book for next year already?', [], null],
  ['Do you do audio guides?', [], null],
  ['Is the Vatican tour skip-the-line?', [102], 'Yes, your guide takes you through the group entrance, so you skip the ticket line.'],
  ['Where are you based?', [], null],
  ['Do you have reviews?', [], null],
  ['Can I book for a wheelchair user?', [], null],
  ['Is there a discount for booking two tours?', [], null],
  ['Do you do cooking classes?', [], null],
  ['What languages do you offer?', [], null],
  ['Can I reserve without paying now?', [], null],
  ['Do you pick up from the cruise port?', [], null],
  ['Is the twilight tour romantic?', [105], null],
  ['Do tours sell out?', [], null],
  ['Can I talk to a real person?', [], null],
];

const SOURCES = { support: ['WhatsApp', 'Email', 'SMS'], widget: ['Web chat widget'] };

// Mostly 1-2 asks, every 7th a little more: the shape clustering actually produces.
const longTailAsks = (i) => {
  if (i % 7 === 0) return 3 + (i % 4);
  if (i % 3 === 0) return 2;
  return 1;
};

function buildLongTail(entries, channel, firstId) {
  return entries.map(([question, tourIds, answer], i) => {
    const asks = longTailAsks(i);
    const last = 1 + ((i * 5) % 28);
    const tours = tourIds.map((id) => P[id]);
    return {
      id: firstId + i,
      kind: 'gap',
      status: 'open',
      channel,
      question,
      ask_count: asks,
      first_asked_ts: ago(last + asks * 3),
      last_asked_ts: ago(last),
      tours,
      booking_specific_hint: false,
      team_answers: answer ? [{ text: answer, count: asks, last_ts: ago(last), agents: [['Giulia', 'Marco', 'Sam'][i % 3]] }] : [],
      examples: [{
        text: question.charAt(0).toLowerCase() + question.slice(1),
        asked_ts: ago(last),
        tour: tours[0]?.name || null,
        source: SOURCES[channel][i % SOURCES[channel].length],
      }],
    };
  });
}

export const SAMPLE_LONG_TAIL = [
  ...buildLongTail(LONG_TAIL_SUPPORT, 'support', 2000),
  ...buildLongTail(LONG_TAIL_WIDGET, 'widget', 3000),
];
